// Estáticos no boot (§2.2.4, §14.2; FE §3.12, DE-B10). `candidates` é o caminho crítico: sem ele o boot não conclui
// (novas tentativas em 2, 4 e 8 s, depois a cada 8 s, até a guarda de 30 s do §12.7). `geography`, `firstRound` e
// `baseline` são promessas preguiçosas: o fetch só sai na primeira leitura do campo (a etapa 3 do §15.7.4 decide quando).
// Os caminhos vêm só de static-manifest.ts (gerado, §2.7.8). Nada aqui lê o relógio do aparelho para decidir dado.
import * as z from 'zod/mini';
import { PLACE_CODES } from '../contracts/base';
import {
  type Baseline2022,
  Baseline2022Schema,
  type CandidateList,
  type FirstRoundResults,
  FirstRoundResultsSchema,
  GeoAbroadCitySchema,
  type Geography,
  GeographyHeadSchema,
  GeographySchema,
  GeoMunicipalitySchema,
  type StaticProvenance,
} from '../contracts/static';
import { type ValidationConfig, validateCandidateList } from '../contracts/validate';
import { STATIC, type StaticKey, type StaticManifest } from './static-manifest';
import { yieldToBrowser } from './yieldTask';

/**
 * Mesma forma de `Statics` (§14.8.1): o `HttpSource` e o `SimSource` devolvem isto em `statics()`. Os três campos
 * paralelos são getters preguiçosos; espalhar o objeto (`{ ...s }`) dispara os três pedidos.
 */
export interface StaticBundle {
  candidates: CandidateList;
  readonly geography: Promise<Geography>;
  readonly firstRound: Promise<FirstRoundResults | null>;
  readonly baseline: Promise<Baseline2022 | null>;
}

export type StaticFailure = 'missing' | 'network' | 'timeout' | 'http' | 'parse' | 'invalid' | 'sample' | 'aborted';

export class StaticLoadError extends Error {
  constructor(
    readonly file: StaticKey,
    readonly reason: StaticFailure,
    readonly detail = '',
  ) {
    super(`estático ${file}: ${reason}${detail ? ` (${detail})` : ''}`);
    this.name = 'StaticLoadError';
  }
  /** Falhas de transporte valem nova tentativa; conteúdo inválido ou ausente do manifesto não muda com o tempo. */
  get retryable(): boolean {
    return this.reason === 'network' || this.reason === 'timeout' || this.reason === 'http';
  }
}

export interface LoadStaticOptions {
  /** round, election_date e mode do AppConfig (I-10 confere o gênero só no 2º turno). */
  config: ValidationConfig;
  /** Build de produção: estático de amostra (`provenance.kind = 'sample'`) é recusado (§14.2, AC-2.7-08). */
  rejectSample?: boolean;
  manifest?: StaticManifest;
  fetchImpl?: typeof fetch;
  /** Espera entre tentativas (injetável nos testes); padrão: setTimeout, interrompida pelo signal. */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  signal?: AbortSignal;
  /** Tempo-limite de cada pedido (§14.8.2: 10 s). */
  timeoutMs?: number;
  /** Aviso de estático opcional ausente ou inválido (AC-14.2-03: só um warn no console). */
  warn?: (message: string) => void;
}

/** §2.2.4: novas tentativas em 2, 4 e 8 s; o caminho crítico continua a cada 8 s depois disso. */
export const RETRY_DELAYS_MS = [2000, 4000, 8000] as const;
export const STATIC_TIMEOUT_MS = 10_000;

function defaultSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('aborted'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error('aborted'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Busca e faz o parse de um estático imutável (`cache: 'default'`, §14.8.2), com tempo-limite. */
async function fetchJson(key: StaticKey, path: string, o: StaticFetchOptions): Promise<unknown> {
  const fetchImpl = o.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const ctrl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, o.timeoutMs ?? STATIC_TIMEOUT_MS);
  const onAbort = () => ctrl.abort();
  o.signal?.addEventListener('abort', onAbort, { once: true });
  try {
    let res: Response;
    try {
      res = await fetchImpl(path, { cache: 'default', redirect: 'error', signal: ctrl.signal });
    } catch (e) {
      if (o.signal?.aborted) throw new StaticLoadError(key, 'aborted');
      throw new StaticLoadError(key, timedOut ? 'timeout' : 'network', (e as Error)?.message ?? '');
    }
    if (!res.ok) throw new StaticLoadError(key, 'http', String(res.status));
    let text: string;
    try {
      text = await res.text();
    } catch (e) {
      throw new StaticLoadError(key, timedOut ? 'timeout' : 'network', (e as Error)?.message ?? '');
    }
    try {
      return JSON.parse(text) as unknown;
    } catch (e) {
      throw new StaticLoadError(key, 'parse', (e as Error).message);
    }
  } finally {
    clearTimeout(timer);
    o.signal?.removeEventListener('abort', onAbort);
  }
}

function checkSample(key: StaticKey, provenance: StaticProvenance, o: Pick<LoadStaticOptions, 'rejectSample'>): void {
  if (o.rejectSample && provenance.kind === 'sample') throw new StaticLoadError(key, 'sample', 'build de produção');
}

type Validator<T> = (raw: unknown) => T | Promise<T>;
/** O que a busca usa: tudo menos o `config`, que só o validador do cadastro lê. */
type StaticFetchOptions = Omit<LoadStaticOptions, 'config'>;

/**
 * Busca, valida e repete nas falhas de transporte. `forever`: depois das 3 novas tentativas, continua a cada 8 s
 * (caminho crítico); sem ele, desiste e devolve o último erro.
 */
async function loadWithRetry<T>(
  key: StaticKey,
  validate: Validator<T>,
  o: StaticFetchOptions,
  forever: boolean,
): Promise<T> {
  const path = (o.manifest ?? STATIC)[key];
  if (path === null) throw new StaticLoadError(key, 'missing', 'sem arquivo no manifesto');
  const sleep = o.sleep ?? defaultSleep;
  for (let attempt = 0; ; attempt++) {
    try {
      return await validate(await fetchJson(key, path, o));
    } catch (e) {
      const err = e instanceof StaticLoadError ? e : new StaticLoadError(key, 'invalid', String(e));
      const last = RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1] as number;
      if (!err.retryable || (!forever && attempt >= RETRY_DELAYS_MS.length)) throw err;
      try {
        await sleep(RETRY_DELAYS_MS[attempt] ?? last, o.signal);
      } catch {
        throw new StaticLoadError(key, 'aborted');
      }
    }
  }
}

/** Valida o cadastro: schema + I-10 (gênero no 2º turno) e pares em ordem; amostra recusada em produção. */
export function parseCandidates(raw: unknown, o: Pick<LoadStaticOptions, 'config' | 'rejectSample'>): CandidateList {
  const checked = validateCandidateList(raw, { config: o.config });
  if (!checked.ok) {
    const detail = checked.errors.map((e) => `${e.code} ${e.path}`).join('; ');
    throw new StaticLoadError('candidates', 'invalid', detail);
  }
  checkSample('candidates', checked.value.provenance, o);
  return checked.value;
}

export function parseGeography(raw: unknown, o: Pick<LoadStaticOptions, 'rejectSample'>): Geography {
  const parsed = GeographySchema.safeParse(raw);
  if (!parsed.success) throw new StaticLoadError('geography', 'invalid', parsed.error.issues[0]?.message ?? '');
  return checkGeography(parsed.data as Geography, o);
}

/** Itens por pedaço da validação da geografia (~5 ms cada no celular com a CPU 4× mais lenta). */
export const GEO_CHUNK = 500;

interface ListCheck<T> {
  safeParse(v: unknown): { success: true; data: T[] } | { success: false; error: { issues: readonly ZodIssueLite[] } };
}
interface ZodIssueLite {
  path: readonly PropertyKey[];
  message: string;
}

/** Uma lista longa da geografia validada em pedaços, devolvendo a vez ao navegador antes de cada um (sem relógio). */
async function parseListInChunks<T>(items: readonly unknown[], check: ListCheck<T>, name: string, size: number) {
  const out: T[] = [];
  for (let i = 0; i < items.length; i += size) {
    await yieldToBrowser();
    const r = check.safeParse(items.slice(i, i + size));
    if (!r.success) {
      const issue = r.error.issues[0];
      const at = i + Number(issue?.path[0] ?? 0);
      throw new StaticLoadError('geography', 'invalid', `${name}[${at}]: ${issue?.message ?? ''}`);
    }
    out.push(...r.data);
  }
  return out;
}

/**
 * A mesma validação de `parseGeography`, em pedaços (DECISOES §44): o cabeçalho de uma vez; municípios e cidades do
 * Exterior em blocos de `GEO_CHUNK`, com os mesmos esquemas de item, devolvendo a vez ao navegador entre eles (a
 * validação inteira de 1,1 MB era uma tarefa longa só, no meio da carga). Tudo ou nada: a geografia só existe, e só é
 * devolvida, depois de o último pedaço passar; qualquer pedaço inválido recusa o arquivo inteiro.
 */
export async function parseGeographyInChunks(
  raw: unknown,
  o: Pick<LoadStaticOptions, 'rejectSample'>,
  size = GEO_CHUNK,
): Promise<Geography> {
  const head = GeographyHeadSchema.safeParse(raw);
  if (!head.success) throw new StaticLoadError('geography', 'invalid', head.error.issues[0]?.message ?? '');
  const municipalities = await parseListInChunks(
    head.data.municipalities,
    z.array(GeoMunicipalitySchema),
    'municipalities',
    size,
  );
  const abroad = await parseListInChunks(head.data.abroad_cities, z.array(GeoAbroadCitySchema), 'abroad_cities', size);
  return checkGeography({ ...head.data, municipalities, abroad_cities: abroad } as Geography, o);
}

/** Contagens e posições do registry, e a recusa da amostra em produção (as duas validações). */
function checkGeography(geo: Geography, o: Pick<LoadStaticOptions, 'rejectSample'>): Geography {
  if (
    geo.municipalities.length !== geo.registry.n_municipalities ||
    geo.abroad_cities.length !== geo.registry.n_abroad ||
    geo.municipalities.some((m, i) => m.idx !== i) ||
    geo.abroad_cities.some((c, i) => c.idx !== i)
  ) {
    throw new StaticLoadError('geography', 'invalid', 'registry e listas não batem (idx ou contagens)');
  }
  checkSample('geography', geo.provenance, o);
  return geo;
}

export function parseFirstRound(raw: unknown, o: Pick<LoadStaticOptions, 'rejectSample'>): FirstRoundResults {
  const parsed = FirstRoundResultsSchema.safeParse(raw);
  if (!parsed.success) throw new StaticLoadError('firstRound', 'invalid', parsed.error.issues[0]?.message ?? '');
  const fr = parsed.data as FirstRoundResults;
  if (fr.election.round !== 1) throw new StaticLoadError('firstRound', 'invalid', 'election.round ≠ 1');
  if ('ZZ' in fr.governor) throw new StaticLoadError('firstRound', 'invalid', 'governador no Exterior');
  if (PLACE_CODES.some((c) => !fr.president.places[c]))
    throw new StaticLoadError('firstRound', 'invalid', 'lugares do presidente incompletos');
  checkSample('firstRound', fr.provenance, o);
  return fr;
}

export function parseBaseline(raw: unknown, o: Pick<LoadStaticOptions, 'rejectSample'>): Baseline2022 {
  const parsed = Baseline2022Schema.safeParse(raw);
  if (!parsed.success) throw new StaticLoadError('baseline2022', 'invalid', parsed.error.issues[0]?.message ?? '');
  const b = parsed.data as Baseline2022;
  checkSample('baseline2022', b.provenance, o);
  return b;
}

/** Estático opcional: ausente ou inválido vira `null` com um único aviso (o bloco que depende dele some, §13.3). */
/** Estático opcional: as novas tentativas de sempre; ausente, fora do ar ou inválido, null com um aviso. */
export async function optional<T>(key: StaticKey, validate: Validator<T>, o: LoadStaticOptions): Promise<T | null> {
  try {
    return await loadWithRetry(key, validate, o, false);
  } catch (e) {
    const err = e as StaticLoadError;
    if (err.reason !== 'aborted') (o.warn ?? console.warn)(`[estáticos] ${err.message}; o bloco que depende dele some`);
    return null;
  }
}

/**
 * Só a geografia, com as novas tentativas de sempre e sem o cadastro de 2026: a página do replay monta o cadastro da
 * noite a partir do manifesto do acervo e não tem `config` de 2026 para validar o daqui.
 */
export function loadGeography(o: Omit<LoadStaticOptions, 'config'>): Promise<Geography> {
  return loadWithRetry('geography', (raw) => parseGeographyInChunks(raw, o), o, false);
}

/**
 * Carrega o cadastro (bloqueia, com novas tentativas) e prepara as promessas preguiçosas dos demais. Rejeita só com
 * `StaticLoadError`: `invalid`/`sample`/`missing` no cadastro (sem nova tentativa: o arquivo é imutável) ou `aborted`.
 */
export async function loadStatics(o: LoadStaticOptions): Promise<StaticBundle> {
  const candidates = await loadWithRetry('candidates', (raw) => parseCandidates(raw, o), o, true);
  let geography: Promise<Geography> | undefined;
  let firstRound: Promise<FirstRoundResults | null> | undefined;
  let baseline: Promise<Baseline2022 | null> | undefined;
  return {
    candidates,
    get geography() {
      geography ??= loadWithRetry('geography', (raw) => parseGeographyInChunks(raw, o), o, false);
      return geography;
    },
    get firstRound() {
      firstRound ??= optional('firstRound', (raw) => parseFirstRound(raw, o), o);
      return firstRound;
    },
    get baseline() {
      baseline ??= optional('baseline2022', (raw) => parseBaseline(raw, o), o);
      return baseline;
    },
  };
}
